import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Calendar, Phone, Video, MapPin, ArrowRight, ArrowLeft, Check,
  CheckCircle2, Building2, Loader2, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  { value: 'Office Cleaning', label: 'Office Cleaning' },
  { value: 'Commercial Cleaning', label: 'Commercial Cleaning' },
  { value: 'Airbnb Turnover Cleaning', label: 'Airbnb Turnover Cleaning' },
  { value: 'Trash-Out & Property Cleanout', label: 'Trash-Out & Property Cleanout' },
];

const CONSULT_TYPES = [
  { value: 'phone', label: 'Phone Call', icon: Phone, desc: '15-min discovery call', time: '~15 min' },
  { value: 'video', label: 'Video Call', icon: Video, desc: 'Virtual walkthrough', time: '~30 min' },
  { value: 'onsite', label: 'On-Site Walkthrough', icon: MapPin, desc: 'In-person assessment', time: '~45 min' },
];

const TIME_SLOTS = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];

function getNextWeekdays(count = 14) {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < count) {
    if (d.getDay() !== 0) {
      days.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return days;
}

export default function ConsultationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const preselectedService = params.get('service') || '';

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: SERVICES.find(s => s.value.toLowerCase().replace(/[^a-z]/g, '').includes(preselectedService.replace(/-/g, '')))?.value || '',
    consultation_type: '',
    preferred_date: '',
    preferred_time: '',
    message: '',
  });

  const dates = getNextWeekdays();
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone || !form.service || !form.consultation_type || !form.preferred_date || !form.preferred_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/consultations`, form);
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 bg-white" data-testid="consultation-success">
        <div className="max-w-lg mx-auto px-6 py-20 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-3">Consultation Requested!</h1>
            <p className="text-zinc-600 mb-2">
              We've received your request for a <strong>{CONSULT_TYPES.find(t => t.value === form.consultation_type)?.label}</strong> about <strong>{form.service}</strong>.
            </p>
            <p className="text-sm text-zinc-500 mb-8">
              Our team will confirm your {form.preferred_date} appointment within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild className="bg-[#F56F05] hover:bg-[#C2410C] text-white rounded-xl px-6">
                <Link to="/">Back to Home</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-xl px-6">
                <Link to="/services">View Services</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="consultation-page">
      {/* Header */}
      <section className="max-w-2xl mx-auto px-6 pt-6 pb-8">
        <Link to="/services" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#F56F05] mb-6 transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Services
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-zinc-900 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900" data-testid="consultation-heading">Schedule a Consultation</h1>
            <p className="text-sm text-zinc-500">Free — no commitment required</p>
          </div>
        </div>
        {/* Progress */}
        <div className="flex gap-2 mt-6">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-all ${step >= s ? 'bg-[#F56F05]' : 'bg-zinc-100'}`} />
          ))}
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-6 pb-20">
        <AnimatedStep show={step === 1}>
          {/* Step 1: Service + Type */}
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-semibold text-zinc-900 mb-3 block">Which service are you interested in?</Label>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="service-options">
                {SERVICES.map(s => (
                  <button key={s.value} onClick={() => update('service', s.value)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      form.service === s.value ? 'border-[#F56F05] bg-[#FFF7ED]' : 'border-zinc-100 hover:border-zinc-200'
                    }`} data-testid={`service-opt-${s.value}`}>
                    <p className="font-semibold text-sm text-zinc-900">{s.label}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-zinc-900 mb-3 block">How would you like to meet?</Label>
              <div className="grid sm:grid-cols-3 gap-2" data-testid="type-options">
                {CONSULT_TYPES.map(t => (
                  <button key={t.value} onClick={() => update('consultation_type', t.value)}
                    className={`text-center p-4 rounded-xl border-2 transition-all ${
                      form.consultation_type === t.value ? 'border-[#F56F05] bg-[#FFF7ED]' : 'border-zinc-100 hover:border-zinc-200'
                    }`} data-testid={`type-opt-${t.value}`}>
                    <t.icon className={`h-5 w-5 mx-auto mb-2 ${form.consultation_type === t.value ? 'text-[#F56F05]' : 'text-zinc-400'}`} />
                    <p className="font-semibold text-sm text-zinc-900">{t.label}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{t.time}</p>
                  </button>
                ))}
              </div>
            </div>

            <Button onClick={() => setStep(2)} disabled={!form.service || !form.consultation_type}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-12 rounded-xl font-semibold" data-testid="next-step-1">
              Continue <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </AnimatedStep>

        <AnimatedStep show={step === 2}>
          {/* Step 2: Date + Time */}
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-semibold text-zinc-900 mb-3 block">Choose a date</Label>
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="date-picker">
                {dates.map(d => {
                  const iso = d.toISOString().split('T')[0];
                  const isSelected = form.preferred_date === iso;
                  const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
                  const dayNum = d.getDate();
                  const month = d.toLocaleDateString('en-US', { month: 'short' });
                  return (
                    <button key={iso} onClick={() => update('preferred_date', iso)}
                      className={`flex-shrink-0 w-[68px] py-3 rounded-xl text-center border-2 transition-all ${
                        isSelected ? 'border-[#F56F05] bg-[#FFF7ED]' : 'border-zinc-100 hover:border-zinc-200'
                      } ${d.getDay() === 6 ? 'opacity-80' : ''}`}
                      data-testid={`date-${iso}`}>
                      <p className={`text-[10px] font-medium ${isSelected ? 'text-[#F56F05]' : 'text-zinc-400'}`}>{dayName}</p>
                      <p className={`text-lg font-bold ${isSelected ? 'text-[#F56F05]' : 'text-zinc-900'}`}>{dayNum}</p>
                      <p className={`text-[10px] ${isSelected ? 'text-[#F56F05]' : 'text-zinc-400'}`}>{month}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-zinc-900 mb-3 block">Pick a time</Label>
              <div className="grid grid-cols-4 gap-2" data-testid="time-picker">
                {TIME_SLOTS.map(t => (
                  <button key={t} onClick={() => update('preferred_time', t)}
                    className={`py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                      form.preferred_time === t ? 'border-[#F56F05] bg-[#FFF7ED] text-[#F56F05]' : 'border-zinc-100 text-zinc-600 hover:border-zinc-200'
                    }`} data-testid={`time-${t}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-xl h-12">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={!form.preferred_date || !form.preferred_time}
                className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white h-12 rounded-xl font-semibold" data-testid="next-step-2">
                Continue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </AnimatedStep>

        <AnimatedStep show={step === 3}>
          {/* Step 3: Contact Info */}
          <div className="space-y-4">
            {/* Summary */}
            <Card className="border-zinc-100 bg-zinc-50">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm text-zinc-900">{form.service}</p>
                  <p className="text-xs text-zinc-500">
                    {CONSULT_TYPES.find(t => t.value === form.consultation_type)?.label} | {form.preferred_date} at {form.preferred_time}
                  </p>
                </div>
                <Badge className="bg-[#FFF7ED] text-[#F56F05] border-[#F56F05]/20 text-[10px]">Free</Badge>
              </CardContent>
            </Card>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-500 mb-1 block">Full Name *</Label>
                <Input value={form.name} onChange={e => update('name', e.target.value)}
                  placeholder="John Smith" className="rounded-xl h-11" data-testid="input-name" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500 mb-1 block">Company (optional)</Label>
                <Input value={form.company} onChange={e => update('company', e.target.value)}
                  placeholder="Your company" className="rounded-xl h-11" data-testid="input-company" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-500 mb-1 block">Email *</Label>
                <Input type="email" value={form.email} onChange={e => update('email', e.target.value)}
                  placeholder="john@company.com" className="rounded-xl h-11" data-testid="input-email" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500 mb-1 block">Phone *</Label>
                <Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)}
                  placeholder="(971) 715-0335" className="rounded-xl h-11" data-testid="input-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Message (optional)</Label>
              <Textarea value={form.message} onChange={e => update('message', e.target.value)}
                placeholder="Tell us about your space, square footage, or any special requirements..."
                rows={3} className="rounded-xl" data-testid="input-message" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1 rounded-xl h-12">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}
                className="flex-1 bg-[#F56F05] hover:bg-[#C2410C] text-white h-12 rounded-xl font-bold" data-testid="submit-consultation">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                Request Consultation
              </Button>
            </div>
          </div>
        </AnimatedStep>
      </section>
    </div>
  );
}

function AnimatedStep({ show, children }) {
  if (!show) return null;
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
      {children}
    </motion.div>
  );
}
